'use client';

import { Pagination } from '@chemist4u/newt-components';
import { buildPageHref, computeTotalPages } from '@lib/util/pagination';
import { useRouter } from 'next/navigation';
import type { MouseEvent, SyntheticEvent } from 'react';
import { useRef } from 'react';

type PlpPaginationProps = {
  pathname: string;
  /** Every other PLP query param (`sortBy`/`minPrice`/`maxPrice`) currently
   * applied — preserved on every page link. Must NOT include `page` itself. */
  currentParams: URLSearchParams;
  page: number;
  pageSize: number;
  totalItems: number;
  /** Navigates to a new PLP URL — pass the caller's `useNavigationLoading().navigate`
   * so its `isPending` state can drive a spinner elsewhere in the same
   * section. Defaults to a plain `router.push` (no pending signal) when
   * omitted. */
  navigate?: (href: string) => void;
};

/** Numbered pager for PLP grids, backed by `@chemist4u/newt-components`'
 * `Pagination`. Renders real, crawlable `<a href>` links (`pageHref`) for
 * every page, and does client-side navigation via `router.push` so real
 * users don't get a full page reload. Changing page jumps instantly to the
 * top of the PAGE (not the grid — deliberate UX decision), with no smooth
 * animation: an instant jump can't "lurch" mid-scroll and needs no
 * `prefers-reduced-motion` handling. */
export default function PlpPagination({
  pathname,
  currentParams,
  page,
  pageSize,
  totalItems,
  navigate: navigateProp,
}: PlpPaginationProps) {
  const router = useRouter();
  // Hrefs already prefetched this mount — `router.prefetch` is not idempotent
  // and pointer events fire repeatedly over the same link.
  const prefetchedHrefs = useRef(new Set<string>());
  const navigate = navigateProp ?? ((href: string) => router.push(href));
  const totalPages = computeTotalPages(totalItems, pageSize);

  if (totalPages <= 1) return null;

  const pageHref = (targetPage: number) =>
    buildPageHref(pathname, targetPage, currentParams);

  const handleNavigate = (
    targetPage: number,
    event: MouseEvent<HTMLAnchorElement>,
  ) => {
    // Let modified/non-primary clicks (Ctrl/Cmd/Shift/Alt, middle-click) fall
    // through to the browser's native "open in new tab/window" behaviour —
    // these are real `<a href>` links, not just click targets.
    if (
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    navigate(pageHref(targetPage));
    window.scrollTo(0, 0);
  };

  // Prefetches the RSC payload for the pager link the pointer/focus is on,
  // so the click itself doesn't wait on a cold server round-trip. Delegated
  // from this wrapper rather than passed to the links: the design system's
  // `Link` hardcodes `prefetch: false` on its `next/link` and `Pagination`
  // exposes no prop to override it. Intent-based, never eager — the
  // paginated PLP route is fully dynamic, so every prefetch is a real Medusa
  // request and the pager renders 5-9 page numbers at a time.
  const prefetchOnIntent = (event: SyntheticEvent) => {
    const target = event.target as HTMLElement | null;
    const href = target?.closest?.('a')?.getAttribute('href');
    if (!href || prefetchedHrefs.current.has(href)) return;
    prefetchedHrefs.current.add(href);
    router.prefetch(href);
  };

  return (
    // `onPointerOver` (not `onPointerEnter`) so the event bubbles up from the
    // individual links; `onFocusCapture` gives keyboard users the same head
    // start as pointer users.
    <div
      className="mt-24 desktop:mx-auto desktop:max-w-[819px]"
      onPointerOver={prefetchOnIntent}
      onFocusCapture={prefetchOnIntent}
    >
      <Pagination
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        previousHref={pageHref(Math.max(1, page - 1))}
        nextHref={pageHref(Math.min(totalPages, page + 1))}
        pageHref={pageHref}
        onNavigate={handleNavigate}
      />
    </div>
  );
}
